import React, { useEffect, useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import {
  LABEL_SIZES,
  POSITIONS,
  Position,
  ContentKey,
  CONTENT_OPTIONS,
  sizeToPx,
  sizeToMm,
  getBarcodeSettings,
  customLabelSize,
  PriceStyle,
} from '../services/barcode';
import { LabelTextRow } from '../services/tspl';
import { collectBarcodes, nextEan13Batch } from '../services/ean';
import { printProductLabels } from '../services/labels';
import { labelPrintingAvailable, printLabelSpec, resolveLabelTarget } from '../services/labelPrint';
import './Sections.css';
import './BarcodePrinting.css';

const BarcodePrinting: React.FC = () => {
  const { products, formatMoney, config, updateProduct } = usePos();
  // Seed label defaults from the saved Barcode Settings.
  const saved = getBarcodeSettings();
  const [productId, setProductId] = useState('');
  // '' = the product itself; 'ALL' = a batch for every variant; else one variant.
  const [variantId, setVariantId] = useState('');
  const [search, setSearch] = useState('');
  const [count, setCount] = useState('1');
  const [size, setSize] = useState(saved.labelSize);
  const [position, setPosition] = useState<Position>(saved.position);
  const [weightable, setWeightable] = useState(false);
  const [withoutHeader, setWithoutHeader] = useState(false);
  const [withoutPrice, setWithoutPrice] = useState(saved.withoutPrice);
  const [priceWithOutlet, setPriceWithOutlet] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [priceStyle, setPriceStyle] = useState<PriceStyle>(saved.priceStyle);
  const [marginTop, setMarginTop] = useState(String(saved.marginTopMm));
  const [marginLeft, setMarginLeft] = useState(String(saved.marginLeftMm));
  const [marginBottom, setMarginBottom] = useState(String(saved.marginBottomMm));
  const [marginRight, setMarginRight] = useState(String(saved.marginRightMm));
  const [across, setAcross] = useState(saved.labelsAcross);
  const [columnGap, setColumnGap] = useState(String(saved.columnGapMm));
  const [labelGap, setLabelGap] = useState(String(saved.labelGapMm));
  // Result of the last "Print USB" attempt, shown next to the buttons.
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const usbTarget = resolveLabelTarget();

  const [content, setContent] = useState<Record<ContentKey, boolean>>(saved.content);
  const toggle = (k: ContentKey) => setContent((c) => ({ ...c, [k]: !c[k] }));

  // Item search: name, short name, product/variant barcode, or the numeric
  // code — all case-insensitive. The selected product stays in the list even
  // when it no longer matches, so the dropdown never loses its own value.
  const term = search.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!term) return products;
    const hit = (v?: string) => !!v && v.toLowerCase().includes(term);
    return products.filter(
      (p) =>
        p.id === productId ||
        hit(p.name) ||
        hit(p.shortName) ||
        hit(p.barcode) ||
        hit(p.searchCode) ||
        String(p.id) === term ||
        p.variants?.some((v) => hit(v.barcode) || hit(v.name))
    );
  }, [products, term, productId]);

  // A scanned barcode (exact match on a product or variant code) selects the
  // item straight away — no need to open the dropdown.
  useEffect(() => {
    const scanned = search.trim();
    if (!scanned) return;
    const exact = products.find(
      (p) => p.barcode === scanned || p.variants?.some((v) => v.barcode === scanned)
    );
    if (!exact || exact.id === productId) return;
    setProductId(exact.id);
    const v = exact.variants?.find((x) => x.barcode === scanned);
    setVariantId(v ? v.id : '');
  }, [search, products, productId]);
  const product = products.find((p) => p.id === productId);
  const productVariants = product?.variants ?? [];
  const variant = productVariants.find((v) => v.id === variantId);
  const labels = Math.max(0, parseInt(count) || 0);
  const dims = sizeToPx(size);

  // The code printed under the bars: manual override > the selected variant's
  // own EAN-13 > the product barcode > its id. Weightable labels use a
  // "2"-prefixed embedded-weight style EAN.
  const baseCode =
    customCode || variant?.barcode || product?.barcode || (product ? String(product.id) : '');
  const code = weightable && baseCode ? `2${baseCode}`.slice(0, 13).padEnd(13, '0') : baseCode;

  const Bars = ({ value }: { value: string }) => (
    <div className="bc-bars">
      {(value || '0').split('').map((ch, idx) => (
        <span key={idx} style={{ width: `${(ch.charCodeAt(0) % 4) + 1}px` }} />
      ))}
    </div>
  );

  // The label as the printers see it, from the current screen selections.
  const labelSpec = (p: NonNullable<typeof product>) => {
    const mm = sizeToMm(size);
    return {
      widthMm: mm.w,
      heightMm: mm.h,
      position,
      header: !withoutHeader && content.shopName ? config.storeName : undefined,
      name: content.productName ? (variant ? `${p.name} · ${variant.name}` : p.name) : undefined,
      rows: rows(p),
      footer: priceWithOutlet || undefined,
      code,
      copies: labels,
      marginTopMm: Math.max(0, parseFloat(marginTop) || 0),
      marginLeftMm: Math.max(0, parseFloat(marginLeft) || 0),
      marginBottomMm: Math.max(0, parseFloat(marginBottom) || 0),
      marginRightMm: Math.max(0, parseFloat(marginRight) || 0),
      across,
      columnGapMm: Math.max(0, parseFloat(columnGap) || 0),
      gapMm: Math.max(0, parseFloat(labelGap) || 0),
    };
  };

  const variantRows = (p: NonNullable<typeof product>) =>
    productVariants.map((v) => ({
      name: `${p.name} · ${v.name}`,
      code: v.barcode || p.barcode || String(p.id),
      price: v.price,
      mrp: p.mrpPrice,
      hsn: p.hsnCode,
    }));

  // Print the labels on the dedicated USB (TSC/TSPL) label printer when one
  // is configured; otherwise fall back to the browser print sheet below.
  const printLabels = () => {
    if (!product || labels < 1) return;
    setStatus(null);
    // "All variants" prints one batch per variant, each with its own code.
    if (variantId === 'ALL' && productVariants.length) {
      printProductLabels(variantRows(product), labels, config.storeName, formatMoney);
      return;
    }
    if (labelPrintingAvailable()) {
      printLabelSpec(labelSpec(product)).catch((e) => {
        console.warn('USB label print failed, falling back to browser print:', e);
        window.print();
      });
      return;
    }
    window.print();
  };

  // "Random" issues the next free EAN-13 and writes it straight to the product
  // (or the selected variant) in Product Master, so the printed label and the
  // catalogue always carry the same code.
  const generateAndSave = () => {
    if (!product) return;
    setStatus(null);
    // "All variants" selected → issue one code per variant in a single batch.
    if (variantId === 'ALL' && productVariants.length) {
      const codes = nextEan13Batch(collectBarcodes(products), productVariants.length);
      updateProduct({
        ...product,
        variants: productVariants.map((v, i) => ({ ...v, barcode: codes[i] })),
      });
      setStatus({
        ok: true,
        text: `${codes.length} barcodes saved to the variants of ${product.name}.`,
      });
      return;
    }
    const [next] = nextEan13Batch(collectBarcodes(products), 1);
    setCustomCode(next);
    const target = variant
      ? {
          ...product,
          variants: productVariants.map((v) => (v.id === variant.id ? { ...v, barcode: next } : v)),
        }
      : { ...product, barcode: next };
    updateProduct(target);
    setStatus({
      ok: true,
      text: `Barcode ${next} saved to ${product.name}${variant ? ` · ${variant.name}` : ''}.`,
    });
  };

  // Save a manually typed barcode to the selected product/variant.
  const saveCustomCode = () => {
    if (!product || !customCode.trim()) return;
    setStatus(null);
    const value = customCode.trim();
    const clash = products.find(
      (p) =>
        p.id !== product.id &&
        (p.barcode === value || p.variants?.some((v) => v.barcode === value))
    );
    if (clash) {
      setStatus({ ok: false, text: `Barcode ${value} is already used by ${clash.name}.` });
      return;
    }
    const target = variant
      ? {
          ...product,
          variants: productVariants.map((v) => (v.id === variant.id ? { ...v, barcode: value } : v)),
        }
      : { ...product, barcode: value };
    updateProduct(target);
    setStatus({
      ok: true,
      text: `Barcode ${value} saved to ${product.name}${variant ? ` · ${variant.name}` : ''}.`,
    });
  };

  // Explicit "Print USB": always goes to the USB printer and reports what
  // happened instead of silently falling back to the browser print sheet.
  const printUsb = async () => {
    if (!product || labels < 1) return;
    setStatus(null);
    const target = resolveLabelTarget();
    if (!target) {
      setStatus({ ok: false, text: 'No USB printer set. Choose one under USB Devices first.' });
      return;
    }
    try {
      if (variantId === 'ALL' && productVariants.length) {
        for (const row of variantRows(product)) {
          // eslint-disable-next-line no-await-in-loop
          await printLabelSpec({ ...labelSpec(product), name: row.name, code: row.code });
        }
      } else {
        await printLabelSpec(labelSpec(product));
      }
      setStatus({
        ok: true,
        text: `Sent ${labels} label${labels > 1 ? 's' : ''} to ${
          target.printer.label || 'the USB printer'
        } (${target.lang === 'tspl' ? 'TSPL' : 'ESC/POS'}).`,
      });
    } catch (e) {
      setStatus({ ok: false, text: (e as Error).message });
    }
  };

  // Title | Value rows (barcode_preview_new.xml) for the enabled content options.
  const rows = (p: NonNullable<typeof product>): LabelTextRow[] => {
    const out: LabelTextRow[] = [];
    if (content.shortName && p.shortName) out.push({ label: 'Short Name', value: p.shortName });
    // Price honours the bold / big-font emphasis chosen below.
    if (content.price && !withoutPrice)
      out.push({ label: 'Price', value: formatMoney(variant?.price ?? p.price), ...priceStyle });
    if (content.mrp) out.push({ label: 'MRP', value: formatMoney(p.mrpPrice ?? p.price) });
    if (content.purchasePrice && p.costPrice != null)
      out.push({ label: 'Pur. Price', value: formatMoney(p.costPrice) });
    if (content.hsn && p.hsnCode) out.push({ label: 'HSN', value: p.hsnCode });
    if (content.searchCode) out.push({ label: 'Code', value: String(p.id) });
    return out;
  };

  // One preview row, rendered with the same emphasis the printer will apply.
  const Row = ({ r }: { r: LabelTextRow }) => (
    <div
      className="bc-row"
      style={{ fontWeight: r.bold ? 700 : undefined, fontSize: r.scale && r.scale > 1 ? `${r.scale * 9}px` : undefined }}
    >
      <span>{r.label}</span>
      <span>{r.value}</span>
    </div>
  );

  return (
    <div className="section-page bc-page">
      <div className="page-header no-print">
        <h1>Barcode Printing</h1>
        <div className="bc-print-actions">
          {status && (
            <span className={status.ok ? 'bc-print-ok' : 'bc-print-err'}>{status.text}</span>
          )}
          <button
            className="btn-teal"
            onClick={printUsb}
            disabled={!product || labels < 1 || !usbTarget}
            title={
              usbTarget
                ? `Print directly to ${usbTarget.printer.label || 'the USB printer'} (${
                    usbTarget.lang === 'tspl' ? 'TSPL' : 'ESC/POS'
                  })`
                : 'No USB printer set — pick one under USB Devices'
            }
          >
            🖨 Print USB
          </button>
          <button className="btn-grey" onClick={printLabels} disabled={!product || labels < 1}>
            Print Labels
          </button>
        </div>
      </div>

      <div className="bc-grid no-print">
        {/* ===== Panel 1: Label Setup ===== */}
        <div className="bc-panel">
          <div className="bc-panel-head">Label Setup</div>
          <div className="bc-panel-body">
            <div className="bc-field-label">Select your barcode position</div>
            <div className="bc-divider" />
            <div className="bc-radio-row">
              {POSITIONS.map((p) => (
                <button key={p} className={`bc-radio ${position === p ? 'active' : ''}`} onClick={() => setPosition(p)}>
                  <span className="bc-radio-dot" />
                  {p}
                </button>
              ))}
            </div>

            <div className="bc-field-label">Labels across the roll</div>
            <div className="bc-divider" />
            <div className="bc-gen-row">
              <select
                className="bc-input"
                value={across}
                onChange={(e) => setAcross(Number(e.target.value))}
              >
                <option value={1}>1 label across</option>
                <option value={2}>2 labels across (2-up)</option>
                <option value={3}>3 labels across</option>
              </select>
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={columnGap}
                title="Gap between the columns, in mm"
                onChange={(e) => setColumnGap(e.target.value)}
              />
            </div>
            {across > 1 && usbTarget?.lang === 'escpos' && (
              <div className="bc-search-hint">
                Side-by-side labels need a TSPL label printer; on ESC/POS they print one after
                another.
              </div>
            )}

            <div className="bc-field-label">Gap between labels (mm)</div>
            <div className="bc-divider" />
            <input
              className="bc-input"
              type="number"
              min={0}
              step={0.5}
              value={labelGap}
              title="The blank strip between two labels on the roll — the printer's gap sensor uses this"
              onChange={(e) => setLabelGap(e.target.value)}
            />

            <div className="bc-field-label">Label margins — top / left / bottom / right (mm)</div>
            <div className="bc-divider" />
            <div className="bc-gen-row">
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={marginTop}
                onChange={(e) => setMarginTop(e.target.value)}
                placeholder="Top"
                title="Blank space at the top of the label"
              />
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={marginLeft}
                onChange={(e) => setMarginLeft(e.target.value)}
                placeholder="Left"
                title="Blank space at the left edge of the label"
              />
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={marginBottom}
                onChange={(e) => setMarginBottom(e.target.value)}
                placeholder="Bottom"
                title="Blank space kept at the bottom of the label — 0 uses the full height"
              />
              <input
                className="bc-input"
                type="number"
                min={0}
                step={0.5}
                value={marginRight}
                onChange={(e) => setMarginRight(e.target.value)}
                placeholder="Right"
                title="Blank space kept at the right edge — 0 uses the full width"
              />
            </div>

            <div className="bc-field-label">Select your label size</div>
            <div className="bc-divider" />
            <div className="bc-size-list">
              {LABEL_SIZES.map((l) => (
                <button key={l} className={`bc-radio ${size === l ? 'active' : ''}`} onClick={() => setSize(l)}>
                  <span className="bc-radio-dot" />
                  {l}
                </button>
              ))}
              {!LABEL_SIZES.includes(size) && (
                <button className="bc-radio active">
                  <span className="bc-radio-dot" />
                  {size} (custom)
                </button>
              )}
            </div>

            <div className="bc-field-label">Or enter your own size (mm)</div>
            <div className="bc-divider" />
            <div className="bc-gen-row">
              <input
                className="bc-input"
                type="number"
                min={10}
                value={sizeToMm(size).w}
                title="Label width in mm"
                onChange={(e) => setSize(customLabelSize(Number(e.target.value) || 0, sizeToMm(size).h))}
              />
              <input
                className="bc-input"
                type="number"
                min={10}
                value={sizeToMm(size).h}
                title="Label height in mm"
                onChange={(e) => setSize(customLabelSize(sizeToMm(size).w, Number(e.target.value) || 0))}
              />
            </div>
          </div>
        </div>

        {/* ===== Panel 2: Customize Content ===== */}
        <div className="bc-panel">
          <div className="bc-panel-head">Customize Content</div>
          <div className="bc-panel-body">
            <div className="bc-field-label">Customize your label content</div>
            <div className="bc-divider" />
            <label className="bc-check">
              <input type="checkbox" checked={withoutPrice} onChange={(e) => setWithoutPrice(e.target.checked)} />
              Without Price Label
            </label>
            <div className="bc-content-list">
              {CONTENT_OPTIONS.map((o) => (
                <label key={o.key} className="bc-check">
                  <input
                    type="checkbox"
                    checked={content[o.key]}
                    disabled={o.key === 'price' && withoutPrice}
                    onChange={() => toggle(o.key)}
                  />
                  {o.label}
                </label>
              ))}
            </div>
            <div className="bc-thickline" />
            <input
              className="bc-input"
              placeholder="Enter Price With Outlet Name"
              value={priceWithOutlet}
              onChange={(e) => setPriceWithOutlet(e.target.value)}
            />
            <label className="bc-check" style={{ marginTop: 10 }}>
              <input type="checkbox" checked={withoutHeader} onChange={(e) => setWithoutHeader(e.target.checked)} />
              Enable Without Header Barcode Label Print.
            </label>

            <div className="bc-field-label">Price emphasis</div>
            <div className="bc-divider" />
            <label className="bc-check">
              <input
                type="checkbox"
                checked={priceStyle.bold}
                disabled={withoutPrice || !content.price}
                onChange={(e) => setPriceStyle((s0) => ({ ...s0, bold: e.target.checked }))}
              />
              Bold Price
            </label>
            <select
              className="bc-input"
              value={priceStyle.scale}
              disabled={withoutPrice || !content.price}
              onChange={(e) =>
                setPriceStyle((s0) => ({ ...s0, scale: Number(e.target.value) as PriceStyle['scale'] }))
              }
            >
              <option value={1}>Normal font</option>
              <option value={2}>Big font (2x)</option>
              <option value={3}>Extra big font (3x)</option>
            </select>
          </div>
        </div>

        {/* ===== Panel 3: Barcode Preview ===== */}
        <div className="bc-panel">
          <div className="bc-panel-head">Barcode Preview</div>
          <div className="bc-panel-body">
            <div className="bc-search">
              <input
                placeholder="Item by Code/Barcode/Name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="bc-search-icon">🔍</span>
            </div>
            {term && (
              <div className="bc-search-hint">
                {matches.length === 0
                  ? 'No items match that name, barcode or code.'
                  : `${matches.length} item${matches.length > 1 ? 's' : ''} — pick one below`}
              </div>
            )}
            <select
              className="bc-input"
              value={productId}
              onChange={(e) => {
                setProductId(e.target.value);
                setVariantId('');
              }}
            >
              <option value="">Select product…</option>
              {matches.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.barcode ? `(${p.barcode})` : ''}
                </option>
              ))}
            </select>

            {productVariants.length > 0 && (
              <select className="bc-input" value={variantId} onChange={(e) => setVariantId(e.target.value)}>
                <option value="">Product label (no variant)</option>
                <option value="ALL">All variants — {productVariants.length} batches</option>
                {productVariants.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} {v.barcode ? `(${v.barcode})` : '(no barcode)'}
                  </option>
                ))}
              </select>
            )}

            <input
              className="bc-input"
              type="number"
              min={1}
              value={count}
              placeholder="Number of Print"
              onChange={(e) => setCount(e.target.value)}
            />

            <label className="bc-check">
              <input type="checkbox" checked={weightable} onChange={(e) => setWeightable(e.target.checked)} />
              Weightable Barcode
            </label>

            <div className="bc-gen-row">
              <input
                className="bc-input"
                placeholder={
                  variant ? 'Barcode No. — saved to this variant' : 'Barcode No. — saved to the product'
                }
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value)}
              />
              <button className="bc-gen-btn" onClick={generateAndSave} disabled={!product}>
                Random
              </button>
              <button className="bc-gen-btn" onClick={saveCustomCode} disabled={!product || !customCode.trim()}>
                Save
              </button>
            </div>

            <div className="bc-field-label">Barcode Label Preview</div>
            <div className="bc-divider" />
            <div className="bc-preview-stage" style={{ gap: `${(parseFloat(columnGap) || 0) * 3.4}px` }}>
              {product ? (
                <>
                {Array.from({ length: across }).map((_, col) => (
                <div
                  key={col}
                  className="bc-label"
                  style={{
                    width: dims.w,
                    minHeight: dims.h,
                    paddingTop: `${(parseFloat(marginTop) || 0) * 3.4}px`,
                    paddingLeft: `${(parseFloat(marginLeft) || 0) * 3.4}px`,
                    paddingBottom: `${(parseFloat(marginBottom) || 0) * 3.4}px`,
                    paddingRight: `${(parseFloat(marginRight) || 0) * 3.4}px`,
                  }}
                >
                  {!withoutHeader && content.shopName && <div className="bc-store">{config.storeName}</div>}
                  {content.productName && <div className="bc-name">{product.name}</div>}
                  {position === 'Bottom' ? (
                    <>
                      {rows(product).map((r) => (
                        <Row r={r} key={r.label} />
                      ))}
                      <Bars value={code} />
                      <div className="bc-code">{code}</div>
                    </>
                  ) : (
                    <>
                      <Bars value={code} />
                      <div className="bc-code">{code}</div>
                      {rows(product).map((r) => (
                        <Row r={r} key={r.label} />
                      ))}
                    </>
                  )}
                  {priceWithOutlet && <div className="bc-store">{priceWithOutlet}</div>}
                </div>
                ))}
                </>
              ) : (
                <span className="muted">Select a product to preview the label.</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print sheet — repeats the label `count` times */}
      {product && (
        <div className="bc-sheet print-only">
          {Array.from({ length: labels }).map((_, i) => (
            <div
              className="bc-label"
              key={i}
              style={{
                width: dims.w,
                minHeight: dims.h,
                paddingTop: `${(parseFloat(marginTop) || 0) * 3.4}px`,
                paddingLeft: `${(parseFloat(marginLeft) || 0) * 3.4}px`,
                paddingBottom: `${(parseFloat(marginBottom) || 0) * 3.4}px`,
                paddingRight: `${(parseFloat(marginRight) || 0) * 3.4}px`,
              }}
            >
              {!withoutHeader && content.shopName && <div className="bc-store">{config.storeName}</div>}
              {content.productName && <div className="bc-name">{product.name}</div>}
              {position === 'Bottom' ? (
                <>
                  {rows(product).map((r) => (
                    <Row r={r} key={r.label} />
                  ))}
                  <Bars value={code} />
                  <div className="bc-code">{code}</div>
                </>
              ) : (
                <>
                  <Bars value={code} />
                  <div className="bc-code">{code}</div>
                  {rows(product).map((r) => (
                    <Row r={r} key={r.label} />
                  ))}
                </>
              )}
              {priceWithOutlet && <div className="bc-store">{priceWithOutlet}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BarcodePrinting;
